import { CodeNameModel } from 'components/common/types';
import { CreativesFilters } from 'components/common/types/Creative.types';
import { PreFilterParseResult } from 'store/pagesFilters/types';
import { parseCreativesStatusParams } from './parseCreativesStatusParams';

const SIMPLE_STRING_PARAMS = [
  'automatic',
  'creativeType',
  'externalId',
  'externalAdvertiserName',
  'clickThroughUrl',
  'externalAgency',
  'width',
  'height',
] as const;

const CODE_NAME_BY_NAME_PARAMS = ['dsp', 'productFormat'] as const;
const CODE_NAME_BY_CODE_PARAMS = ['advertiser', 'brand', 'productCategory'] as const;
const DATE_PARAMS = ['startDate', 'endDate'] as const;
const CONSUME_ONLY_PARAMS = ['source'] as const;

const ALL_KNOWN_PARAMS = [
  'status',
  'statusFilter',
  ...SIMPLE_STRING_PARAMS,
  ...CODE_NAME_BY_NAME_PARAMS,
  ...CODE_NAME_BY_CODE_PARAMS,
  ...DATE_PARAMS,
  'deleted',
  'dealIds',
] as const;

const toCodeNameByName = (value: string): CodeNameModel => ({ code: '', name: value });
const toCodeNameByCode = (value: string): CodeNameModel => ({ code: value, name: value });

export const parseCreativesPreFilters = (params: URLSearchParams): PreFilterParseResult<CreativesFilters> | null => {
  const filters: Record<string, unknown> = {};
  const consumedParams: string[] = [];

  const { filters: statusFilters, consumedParams: statusConsumedParams } = parseCreativesStatusParams(params);

  Object.assign(filters, statusFilters);
  consumedParams.push(...statusConsumedParams);

  SIMPLE_STRING_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value !== null) {
      filters[key] = value;
      consumedParams.push(key);
    }
  });

  const deleted = params.get('deleted');
  if (deleted !== null) {
    filters.deleted = deleted === 'true';
    consumedParams.push('deleted');
  }

  CODE_NAME_BY_NAME_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value !== null) {
      filters[key] = toCodeNameByName(value);
      consumedParams.push(key);
    }
  });

  CODE_NAME_BY_CODE_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value !== null) {
      filters[key] = toCodeNameByCode(value);
      consumedParams.push(key);
    }
  });

  const dealIds = params.get('dealIds');
  if (dealIds !== null) {
    filters.dealIds = dealIds.split(',').map(toCodeNameByCode);
    consumedParams.push('dealIds');
  }

  DATE_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value !== null) {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        filters[key] = date;
      }
      consumedParams.push(key);
    }
  });

  consumedParams.push(...CONSUME_ONLY_PARAMS.filter((key) => params.has(key)));

  if (consumedParams.length === 0) return null;

  return {
    filters: filters as Partial<CreativesFilters>,
    consumedParams,
  };
};

export const CREATIVES_FILTER_PARAM_NAMES = ALL_KNOWN_PARAMS;
